import algoliasearch from "algoliasearch/lite";
import { subDays } from "date-fns";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";
import {
  CALLS_DEEPLINK_FETCH,
  CALLS_DEEPLINK_FETCH_FAILURE,
  CALLS_DEEPLINK_FETCH_SUCCESS,
  CALLS_FETCH,
  CALLS_FETCH_FAILURE,
  CALLS_FETCH_SUCCESS,
  CALLS_SELECTION_SET,
} from "../actionTypes";
import { auth } from "../../conf/firebase";
import { transformToUTC } from "../../utils/dateUtils";

// Fetch calls between start date and end date
export const getCallsInFirebase = ({ start_date, end_date }) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CALLS_FETCH });
      // Format dates using toISOString
      const formattedStartDate = new Date(start_date).toISOString();
      const formattedEndDate = new Date(end_date).toISOString();

      const base_url = `${getConfig().API_URL}/audio/api/v1/calls`;

      // Make Axios request to fetch calls data from the backend
      const response = await myAxios.get(
        `${base_url}?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
      const { calls } = response.data[0];

      // end
      dispatch({ type: CALLS_FETCH_SUCCESS, payload: calls });
    } catch (error) {
      console.log({ error });
      dispatch({ type: CALLS_FETCH_FAILURE, payload: error.message });
      throw error;
    }
  };
};

// Id of the latest getCallsInAlgolia request. Filters / search text can change
// while a request is in flight: every change must fetch, and only the latest
// response may land in the store (an older one resolving last would show results
// for stale filters).
let latestCallsRequestId = 0;

// Fetch calls between start date and end date
export const getCallsInAlgolia = (
  searchText = "",
  filters = {},
  page = 0,
  hitCount = 12,
  sortDescriptor = { sortBy: "call_timestamp", direction: "desc" },
  isLiveContentSearch = false,
  widgetFilters = null,
  display_tz
) => {
  return async (dispatch) => {
    const { currentUser } = auth;

    if (filters?.start_date) {
      filters.start_date = transformToUTC(filters?.start_date, display_tz);
    }

    if (filters?.end_date) {
      filters.end_date = transformToUTC(filters?.end_date, display_tz);
    }

    if (!currentUser || currentUser === null) {
      console.error("Not fetching deeplink call, user not authenticated");
      return;
    }
    latestCallsRequestId += 1;
    const requestId = latestCallsRequestId;
    try {
      dispatch({ type: CALLS_FETCH });

      const baseUrl = `${getConfig().API_URL}/audio/api/v1/calls`;

      const response = await myAxios.post(`${baseUrl}/algolia_calls`, {
        searchText,
        filters,
        page,
        hitCount,
        sortDescriptor,
        isLiveContentSearch,
        widgetFilters,
      });

      if (requestId !== latestCallsRequestId) return;

      dispatch({
        type: CALLS_FETCH_SUCCESS,
        payload: response?.data?.calls,
        algoliaTotalPages: response?.data?.totalPages,
        algoliaTotalItems: response?.data?.totalItems,
      });
    } catch (error) {
      if (requestId !== latestCallsRequestId) return;
      console.log({ error });
      dispatch({ type: CALLS_FETCH_FAILURE, payload: error.message });
      throw error;
    }
  };
};

export const fetchDeepLinkCallInAlgolia = (callId) => {
  return async (dispatch, getState) => {
    const { currentUser } = auth;

    if (!currentUser || currentUser === null) {
      console.error("Not fetching deeplink call, user not authenticated");
      return;
    }

    try {
      const { loading } = getState().calls;
      if (loading) {
        return;
      }
      dispatch({ type: CALLS_DEEPLINK_FETCH });

      const base_url = `${getConfig().API_URL}/audio/api/v1/calls`;

      const response = await myAxios.get(`${base_url}/${callId}`);
      const foundCall = response.data[0];

      // end
      dispatch({
        type: CALLS_DEEPLINK_FETCH_SUCCESS,
        payload: foundCall,
      });
    } catch (error) {
      console.log({ error });
      dispatch({ type: CALLS_DEEPLINK_FETCH_FAILURE, payload: error.message });
      throw error;
    }
  };
};

// Fetch calls between start date and end date
export const getCallInfo = ({ call }) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: CALLS_FETCH });
      const { calls } = getState().calls.calls;
      // Format dates using toISOString
      // const formattedStartDate = new Date(start_date).toISOString();
      // const formattedEndDate = new Date(end_date).toISOString();

      // TODO: fix build url depending on env
      const base_url = `${getConfig().API_URL}/audio/api/v1/calls`;

      // Make Axios request to fetch calls data from the backend
      const response = await myAxios.get(`${base_url}/${call.id}`);
      const data = response.data[0];
      let updated_calls = calls.map((call_item) =>
        call_item.id === data.id ? { ...call_item, ...data, fetched_info: true } : call_item
      );
      // end
      dispatch({ type: CALLS_FETCH_SUCCESS, payload: updated_calls });
    } catch (error) {
      console.log({ error });
      dispatch({ type: CALLS_FETCH_FAILURE, payload: error.message });
      throw error;
    }
  };
};

const deduplicateArray = (items) => {
  const seen = {};

  return items.filter((item) => {
    const { id } = item;
    const isNew = !seen[id];
    seen[id] = true;
    return isNew;
  });
};

export const addSelectedCall = (item) => {
  return async (dispatch, getState) => {
    const { selectedCalls } = getState().calls.calls;

    const newSelectedCalls = [...(selectedCalls || []), ...[item]];
    dispatch({ type: CALLS_SELECTION_SET, payload: deduplicateArray(newSelectedCalls) });
  };
};

export const removeSelectedCall = (item) => {
  return async (dispatch, getState) => {
    const { selectedCalls } = getState().calls.calls;
    const filteredCalls = [...selectedCalls].filter((selectedItem) => selectedItem.id !== item.id);

    dispatch({ type: CALLS_SELECTION_SET, payload: filteredCalls });
  };
};

export const addSelectedCalls = (items) => {
  return async (dispatch, getState) => {
    const { selectedCalls } = getState().calls.calls;

    const newSelectedCalls = [...(selectedCalls || []), ...items];
    dispatch({ type: CALLS_SELECTION_SET, payload: deduplicateArray(newSelectedCalls) });
  };
};

export const removeSelectedCalls = (items) => {
  return async (dispatch, getState) => {
    const { selectedCalls } = getState().calls.calls;
    const itemsIds = items.map((item) => item.id);

    const newSelectedCalls = [...(selectedCalls || [])].filter(
      (selectedCall) => !itemsIds.includes(selectedCall.id)
    );
    dispatch({ type: CALLS_SELECTION_SET, payload: newSelectedCalls });
  };
};

export const resetSelectedCalls = () => {
  return async (dispatch, getState) => {
    dispatch({ type: CALLS_SELECTION_SET, payload: [] });
  };
};
